import { useCallback } from 'react';
import { Modal, Text } from '@nimbus-ds/components';
import { useNavegate } from 'App/hooks';
import { SubmitButton } from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import { Format, dateFormat } from 'commons/utils/date';
import { usePlanDowngrade } from 'domains/Billing/BillingPlans/hooks';
import { useDeleteScheduledPlanDowngradeMutation } from 'domains/Billing/Checkout/CheckoutApi';
import { trackingBillingCancelScheduledDowngradeConfirm } from 'domains/Billing/tracking';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';

interface CancelDowngradeModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CancelDowngradeModal({
  open,
  onClose,
}: Readonly<CancelDowngradeModalProps>) {
  const t = useTranslationBilling('billingPlansPage.cancelDowngradeModal');
  const { scheduledDowngrade, oldPlanName } = usePlanDowngrade();

  const [deleteScheduledPlanDowngrade, { status: rtkStatus, isLoading }] =
    useDeleteScheduledPlanDowngradeMutation();
  const { status } = combineStatus(rtkStatus);
  const { pathname } = useNavegate();

  const handleCancelDowngrade = useCallback(async () => {
    trackingBillingCancelScheduledDowngradeConfirm(pathname);
    await deleteScheduledPlanDowngrade();
    onClose();
  }, [deleteScheduledPlanDowngrade, onClose, pathname]);

  useToastStatus({
    status,
    error: t('error'),
  });

  if (!scheduledDowngrade) return null;

  return (
    <Modal open={open} onDismiss={onClose}>
      <Modal.Header title={t('title')} />
      <Modal.Body padding="none">
        <Text>
          {t('message', {
            oldPlanName,
            scheduledDate: dateFormat(
              scheduledDowngrade.scheduledAt,
              Format.DD_MM,
            ),
          })}
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton
          isLoading={isLoading}
          onClick={handleCancelDowngrade}
          appearance="primary"
        >
          {t('confirmButton')}
        </SubmitButton>
      </Modal.Footer>
    </Modal>
  );
}
